import type { CombatantState } from './types';

/**
 * Pure health/damage math (spec 0025) — the combat analogue of
 * src/movement/math.ts: no state, no scene, deterministic.
 */

export interface DamageResult {
  /** Damage actually applied (clamped to remaining health; 0 if already dead). */
  applied: number;
  /** True when this application dropped the target to 0. */
  lethal: boolean;
}

/** Applies damage in place. The already-dead take nothing. */
export function applyDamage(state: CombatantState, amount: number): DamageResult {
  if (!state.alive || amount <= 0) {
    return { applied: 0, lethal: false };
  }
  const applied = Math.min(state.health, amount);
  state.health -= applied;
  const lethal = state.health <= 0;
  if (lethal) {
    state.health = 0;
    state.alive = false;
  }
  return { applied, lethal };
}

/**
 * Counts a dead combatant's respawn timer down by `dt`; revives at full health
 * when it reaches zero. Returns true on the revive tick.
 */
export function tickRespawn(state: CombatantState, maxHealth: number, dt: number): boolean {
  if (state.alive || state.respawnIn <= 0) {
    return false;
  }
  state.respawnIn -= dt;
  if (state.respawnIn > 0) {
    return false;
  }
  state.respawnIn = 0;
  state.health = maxHealth;
  state.alive = true;
  return true;
}
